import Dexie from 'dexie';
import moment from 'moment';
import { trackTrace } from 'TelemetryService';
import { getCookie, setCookie } from 'services/utils/generic.methods';

export const db = () => {
  let _apexChatDb = null;
  let _dbName = 'ApexChat_';
  const generateQuickGuid = () => {
    return (
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    );
  };

  if (!window.localStorage.Profile) {
    var dbName = getCookie('dbName');
    if (dbName) {
      _dbName = dbName;
    } else {
      _dbName += generateQuickGuid();
      setCookie('dbName', _dbName, 1);
    }
  } else {
    var profileData = JSON.parse(window.localStorage.Profile);
    _dbName += profileData.UserID + '_' + profileData.CompanyID;
  }
  const init = () => {
    _apexChatDb = new Dexie(_dbName).version(3).stores({
      optInLeads: '++id, chatId, contactId, leadId, jsonData',
      activeChats: '++id, chatId, jsonData',
      allChats: '++id, chatId, jsonData',
      chatLogs: '++id, chatId, chatLogId, messageJson'
    });

    // Define a migration to add the 'createdOn' index
    _apexChatDb.db
      .version(4)
      .stores({
        chatLogs: '++id, chatId, chatLogId, createdOn, messageJson'
      })
      .upgrade(async trans => {
        // If you have existing data, you may need to populate the 'createdOn' field
        await trans.chatLogs.toCollection().modify(log => {
          // Set 'createdOn' to some value based on your data
          log.createdOn = moment().toDate();
        });
      });
  };

  const _chatDbService = table => {
    const _getAll = async () => {
      return await table.toArray();
    };
    const _getByKey = async (key, value) => {
      return await table.where(key).equals(value).toArray();
    };

    const _insertRow = async (chatId, data) => {
      var exists = await _getByKey('chatId', chatId);
      if (!exists || !exists.length) {
        await table.add(data);
      }
    };
    const _removeRow = async chatId => {
      var record = await _getByKey('chatId', chatId);
      if (record && record.length) {
        await table.delete(record[0].id);
      }
    };

    const _updateColumnValue = async (chatId, obj) => {
      var record = await table.where('chatId').equals(chatId);
      if (record) {
        await record.modify(obj);
      }
    };
    const _updateColumnValueByKey = async (key, id, obj) => {
      if (id) {
        var record = await table.where(key).equals(id);
        if (record) {
          await record.modify(obj);
        }
      }
    };

    return {
      getAll: _getAll,
      getByKey: _getByKey,
      insertRow: _insertRow,
      removeRow: _removeRow,
      updateChatColumn: _updateColumnValue,
      updateColumnByKey: _updateColumnValueByKey
    };
  };

  const _chatLogsDbService = () => {
    var table = _apexChatDb.db.chatLogs;
    const _getChatLogs = async chatId => {
      return await table.where('chatId').equals(chatId).toArray();
    };
    const _bulkUpsertLogs = async logs => {
      const rows = (logs || []).filter(
        data => data?.id != null && data.chatId != null
      );
      if (!rows.length) return;

      const existing = await table
        .where('chatLogId')
        .anyOf(rows.map(data => data.id))
        .toArray();
      const byLogId = new Map(existing.map(row => [row.chatLogId, row]));
      const toAdd = [];
      const toPut = [];

      rows.forEach(data => {
        const prev = byLogId.get(data.id);
        if (!prev) {
          toAdd.push({
            chatId: data.chatId,
            chatLogId: data.id,
            createdOn: data?.createdOn,
            messageJson: data
          });
          return;
        }
        toPut.push({
          ...prev,
          chatId: data.chatId,
          chatLogId: data.id,
          createdOn: data?.createdOn || prev.createdOn,
          messageJson: {
            ...prev.messageJson,
            ...data,
            visitorAttachedFiles:
              data.visitorAttachedFiles ||
              prev.messageJson?.visitorAttachedFiles
          }
        });
      });

      const payload = toPut.concat(toAdd);
      if (payload.length) await table.bulkPut(payload);
    };
    const _insertLog = async (chatId, data) => {
      if (!data?.id) {
        const channels = window.RTC?.subscribedChannels || [];
        trackTrace(
          'incomplete chat payload in insertChatLog',
          {
            argChatId: chatId,
            dataKeys:
              data && typeof data === 'object' ? Object.keys(data) : typeof data,
            rtcStatus: window.RTC?.status,
            rtcInit: window.RTC?.init,
            online: navigator.onLine,
            isArgChatSubscribed:
              chatId != null && channels.includes('/chatroom/' + chatId),
            selectedChatId: window.GlobalSelectedId,
            subscribedCount: channels.length,
            data
          },
          2
        );
        return;
      }

      const exists = await table.where('chatLogId').equals(data.id).first();

      if (!exists) {
        // Create new record
        await table.add({
          chatId: data.chatId,
          chatLogId: data.id,
          createdOn: data?.createdOn,
          messageJson: data
        });
      } else {
        // Update existing record
        await table.update(exists.id, {
          ...exists,
          messageJson: {
            ...exists.messageJson,
            ...data,
            visitorAttachedFiles:
              data.visitorAttachedFiles ||
              exists.messageJson.visitorAttachedFiles
          }
        });
      }
    };  
    const _markLogsAsRead = async chatId => {
      await table
        .where('chatId')
        .equals(chatId)
        .modify(t => {
          t.messages?.forEach(x => {
            x.isUnread = false;
          });
        });
    };

    const _removeOldLogs = async days => {
      let newDate = moment().subtract(days, 'days').toDate();
      try {
        var logs = await table.where('createdOn').below(newDate).toArray();
        if (logs.length) {
          // filtered out logs with 'unique' chatId
          let tempLogs = logs.filter((item, index, context) => {
            return context.findIndex(i => i.chatId === item.chatId) === index;
          });

          if (tempLogs.length) {
            // delete all chat logs related to chatId inside tempLogs
            var allLogs = await tempLogs.map(
              async log =>
                await table.where('chatId').equals(log?.chatId).delete()
            );
            Promise.all(allLogs).then(res => {
              console.log('all logs deleted', res);
            });
          }
        }
        return true;
      } catch (error) {
        console.log('error deleting chatlogs', error);
        return false;
      }
    };

    return {
      getAllLogs: _getChatLogs,
      insertChatLog: _insertLog,
      bulkUpsertChatLogs: _bulkUpsertLogs,
      markChatAsRead: _markLogsAsRead,
      removeOldLogs: _removeOldLogs
    };
  };
  const _deleteOnChatEnd = async chatId => {
    var exists = await _apexChatDb.db.activeChats
      .where('chatId')
      .equals(chatId)
      .toArray();
    if (!exists || !exists.length) {
      await _apexChatDb.db.activeChats.delete(exists[0].id);
    }
    exists = await _apexChatDb.db.allChats
      .where('chatId')
      .equals(chatId)
      .toArray();
    if (!exists || !exists.length) {
      await _apexChatDb.db.allChats.delete(exists[0].id);
    }
  };

  init();
  return {
    apexChatDb: _apexChatDb.db,
    optInLeadsDbService: _chatDbService(_apexChatDb.db.optInLeads),
    activeChatsDbService: _chatDbService(_apexChatDb.db.activeChats),
    allChatsDbService: _chatDbService(_apexChatDb.db.allChats),
    chatLogsDbService: _chatLogsDbService(),
    deleteChatOnEnd: _deleteOnChatEnd
  };
};
